<script setup lang="ts">
import type {
  TApplianceCountByProgrammingCategory,
  TSectionApplianceCategory,
} from "#/ui-kit/components/section/appliances/section-appliance-category.types";

import { getCategoryTranslationKey } from "#/ui-kit/components/section/appliances/section-appliance-category-utils";
import {
  categoryOrder,
  EOtherApplianceCategory,
  PROGRAM_APPLIANCE_CATEGORY,
} from "#/ui-kit/components/section/appliances/section-appliance-category.types";
import SectionApplianceIcon from "#/ui-kit/components/section/appliances/section-appliance-icon.vue";
import { useCommonFrontendIntl } from "#/ui-kit/locales/typed-intl";
import { cn, DATA_TEST_IDS, isDefined } from "@voltalis/frontend-utils";
import { computed } from "vue";

interface IProps {
  applianceCountByProgrammingCategory: TApplianceCountByProgrammingCategory;
  showCount?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  showCount: true,
});

const { t } = useCommonFrontendIntl();

const summedCategories = computed<Record<TSectionApplianceCategory, number>>(() => {
  const result: Record<TSectionApplianceCategory, number> = {
    [PROGRAM_APPLIANCE_CATEGORY.HEATER]: 0,
    [PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER]: 0,
    [PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED]: 0,
    [EOtherApplianceCategory.OTHER]: 0,
  };

  for (const [category, maybeCategoryValue] of Object.entries(props.applianceCountByProgrammingCategory)) {
    const categoryValue = maybeCategoryValue ?? 0;

    switch (category) {
      case PROGRAM_APPLIANCE_CATEGORY.HEATER:
        result[PROGRAM_APPLIANCE_CATEGORY.HEATER] = categoryValue;
        break;
      case PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER:
        result[PROGRAM_APPLIANCE_CATEGORY.WATER_HEATER] = categoryValue;
        break;
      case PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED:
        result[PROGRAM_APPLIANCE_CATEGORY.AC_UNIT_NON_INFRARED] = categoryValue;
        break;
      default:
        result[EOtherApplianceCategory.OTHER] += categoryValue;
    }
  }

  return result;
});

function getMaybeCategoryValue(category: TSectionApplianceCategory): number | undefined {
  return summedCategories.value[category];
}
</script>

<template>
  <div
    :class="cn('flex items-center', [props.showCount ? 'gap-3' : 'gap-1'])"
    :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesCountByProgrammingCategory.component"
  >
    <template
      v-for="category in categoryOrder"
      :key="category"
    >
      <template v-for="categoryValue in [getMaybeCategoryValue(category)].filter(isDefined)">
        <div
          v-if="categoryValue"
          class="flex items-center gap-1"
          :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesCountByProgrammingCategory.categoryItem"
          :data-appliance-category="category"
        >
          <div
            :title="t(getCategoryTranslationKey(category))"
            :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesCountByProgrammingCategory.icon"
          >
            <SectionApplianceIcon :sectionApplianceCategory="category" />
          </div>
          <span
            v-if="props.showCount"
            class="text-gray-500"
            :data-test-id="DATA_TEST_IDS.components.siteSectionAppliancesCountByProgrammingCategory.categoryText"
            >{{ categoryValue }}</span
          >
        </div>
      </template>
    </template>
  </div>
</template>
